import React, {
  useMemo,
  useState,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { MotifTable } from '@ey-xd/motif-react';
import {
  handleFilterChanged,
  handleGridReady,
  handleItemsPerPageChange,
} from '../../common/AgGridEventHandlers';
import CustomPagination from '../../common/MotifPagination';
import CustomHeaderTemplate from '../shared/customHeaders/CustomHeaderTemplate';
import MasterDataManagementREITsCellRenderer from '../shared/customRenderers/MasterDataManagementREITsCellRenderer';
import DateComparator from '../shared/customComparators/DateComparator';
import * as formatters from '../../scripts/formFormatters';

const MasterREITsTable = ({
  reitsData,
  handleEditButtonClick,
  handleDeleteButtonClick,
  handleGridChanges,
  gridColumnState,
  currentUserIsSysAdmin,
  currentUserIsEngAdmin,
}) => {
  const dateOptions = {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  };

  const gridRef = useRef();
  const [rowData, setRowData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);
  //Default column settings
  const defaultColDef = useMemo(() => {
    return {
      flex: 1,
      minWidth: 65,
      filter: true,
      resizable: true,
      sortable: true,
      wrapText: true,
      autoHeight: true,
    };
  }, []);

  // Instruction text for the overlay
  const instructionText =
    'Click on Actions > Click Export REIT Import Template > Update the downloaded file with the REIT details > Click Upload REIT Import Template.';

  const columnDefs = useMemo(
    () => [
      {
        field: 'isActive',
        headerName: 'Active',
        sortable: true,
        showRowGroup: false,
        hide: false,
        rowGroup: false,
        minWidth: 150,
        cellRenderer: MasterDataManagementREITsCellRenderer,
        valueGetter: (params) => {
          return params.data?.isActive && params.data?.isActive === true
            ? 'Yes'
            : 'No';
        },
        headerComponentParams: { template: CustomHeaderTemplate('Active') },
      },
      {
        field: 'entityGroupName',
        headerName: 'Group Name',
        sortable: true,
        showRowGroup: false,
        hide: false,
        rowGroup: false,
        minWidth: 200,
        valueGetter: (params) => {
          return params.data?.entityGroupName || '';
        },
        headerComponentParams: { template: CustomHeaderTemplate('Group') },
      },
      {
        field: 'reitTrackingID',
        headerName: 'REITSuite Entity ID',
        sortable: true,
        showRowGroup: false,
        hide: false,
        rowGroup: false,
        minWidth: 200,
        valueGetter: (params) => {
          return params.data?.reitTrackingID || '';
        },
        headerComponentParams: { template: CustomHeaderTemplate('TrackingNo') },
      },
      {
        field: 'gtpEntityID',
        headerName: 'GTP Entity ID',
        sortable: true,
        showRowGroup: false,
        hide: false,
        rowGroup: false,
        minWidth: 165,
        valueGetter: (params) => {
          return params.data?.gtpEntityID || '';
        },
        headerComponentParams: {
          template: CustomHeaderTemplate('GTPEntityID'),
        },
      },
      {
        field: 'clientEntityID',
        headerName: 'Client Entity ID',
        sortable: true,
        showRowGroup: false,
        hide: false,
        rowGroup: false,
        minWidth: 170,
        valueGetter: (params) => {
          return params.data?.clientEntityID || '';
        },
        headerComponentParams: {
          template: CustomHeaderTemplate('ClientEntityID'),
        },
      },
      {
        field: 'reitShortName',
        headerName: 'REIT Name',
        sortable: true,
        showRowGroup: false,
        hide: false,
        rowGroup: false,
        minWidth: 310,
        valueGetter: (params) => {
          return params.data?.reitShortName || '';
        },
        headerComponentParams: { template: CustomHeaderTemplate('REITName') },
      },
      {
        field: 'initialQuarter',
        headerName: 'Initial Quarter',
        sortable: true,
        showRowGroup: false,
        hide: false,
        rowGroup: false,
        minWidth: 170,
        valueGetter: (params) => {
          return params.data?.initialQuarter || '';
        },
        headerComponentParams: {
          template: CustomHeaderTemplate('InitialQuarter'),
        },
      },
      {
        field: 'ein',
        headerName: 'EIN',
        sortable: true,
        showRowGroup: false,
        hide: false,
        rowGroup: false,
        minWidth: 170,
        headerComponentParams: { template: CustomHeaderTemplate('EIN') },
      },
      {
        field: 'symbol',
        headerName: 'REIT Symbol',
        sortable: true,
        showRowGroup: false,
        hide: false,
        rowGroup: false,
        minWidth: 200,
        valueGetter: (params) => {
          return params.data?.symbol || '';
        },
        headerComponentParams: { template: CustomHeaderTemplate('REITSymbol') },
      },
      {
        field: 'reitTypeDescription',
        headerName: 'REIT Type',
        sortable: true,
        showRowGroup: false,
        hide: false,
        rowGroup: false,
        minWidth: 180,
        valueGetter: (params) => {
          return params.data?.reitTypeDescription || '';
        },
        headerComponentParams: { template: CustomHeaderTemplate('REITType') },
      },
      {
        field: 'organizationTypeDescription',
        headerName: 'Organization Type',
        sortable: true,
        showRowGroup: false,
        hide: false,
        rowGroup: false,
        minWidth: 230,
        valueGetter: (params) => {
          return params.data?.organizationTypeDescription || '';
        },
        headerComponentParams: { template: CustomHeaderTemplate('OrgType') },
      },
      {
        field: 'formationDate',
        headerName: 'Formation Date',
        sortable: true,
        showRowGroup: false,
        hide: false,
        rowGroup: false,
        minWidth: 180,
        valueGetter: (params) => {
          const dateValue =
            params.data && params.data.formationDate
              ? params.data.formationDate
              : null;
          return dateValue ? formatters.formatDate(dateValue) : '';
        },
        comparator: DateComparator,
        headerComponentParams: {
          template: CustomHeaderTemplate('FormationDt'),
        },
      },
      {
        field: 'percentageThreshold',
        headerName: 'Trial Balance Threshold',
        sortable: true,
        showRowGroup: false,
        hide: false,
        rowGroup: false,
        minWidth: 300,
        cellRenderer: MasterDataManagementREITsCellRenderer,
        valueGetter: (params) => {
          return (
            params.data &&
            params.data.percentageThreshold +
              '% / $' +
              params.data.balanceThreshold
          );
        },
        headerComponentParams: {
          template: CustomHeaderTemplate('TBThreshold'),
        },
      },
      {
        field: 'stateOrganized',
        headerName: 'State Organized In',
        sortable: true,
        showRowGroup: false,
        hide: false,
        rowGroup: false,
        minWidth: 220,
        valueGetter: (params) => {
          return params.data?.stateOrganized || '';
        },
        headerComponentParams: { template: CustomHeaderTemplate('StOrgIn') },
      },
      {
        field: 'dateOfElection',
        headerName: 'Date of REIT Election',
        sortable: true,
        showRowGroup: false,
        hide: false,
        rowGroup: false,
        minWidth: 190,
        valueGetter: (params) => {
          const dateValue =
            params.data && params.data.dateOfElection
              ? params.data.dateOfElection
              : null;
          return dateValue ? formatters.formatDate(dateValue) : '';
        },
        comparator: DateComparator,
        headerComponentParams: {
          template: CustomHeaderTemplate('REITElectDt'),
        },
      },
      {
        field: 'numberOfSharesClasses',
        headerName: '# Share Classes',
        sortable: true,
        showRowGroup: false,
        hide: false,
        rowGroup: false,
        minWidth: 220,
        valueGetter: (params) => {
          return params.data?.numberOfSharesClasses || 0;
        },
        headerComponentParams: {
          template: CustomHeaderTemplate('NumberOfSharesClasses'),
        },
      },
      {
        field: 'preferredNumberOfShareHolders',
        headerName: '# Preferred Shareholders',
        sortable: true,
        showRowGroup: false,
        hide: false,
        rowGroup: false,
        minWidth: 220,
        valueGetter: (params) => {
          return params.data?.preferredNumberOfShareHolders || 0;
        },
        headerComponentParams: {
          template: CustomHeaderTemplate('PreferredNumberOfShareHolders'),
        },
      },
      {
        field: 'lastUpdatedBy',
        headerName: 'Last Updated By',
        sortable: true,
        showRowGroup: false,
        hide: true,
        rowGroup: false,
        minWidth: 198,
        valueGetter: (params) => {
          return params.data && params.data.lastUpdatedBy;
        },
        filterValueGetter: (params) => {
          return params.data && params.data.lastUpdatedBy;
        },
        headerComponentParams: {
          template: CustomHeaderTemplate('LastUpdatedBy'),
        },
      },
      {
        field: 'lastUpdatedDate',
        headerName: 'Last Updated Date',
        sortable: true,
        showRowGroup: false,
        hide: true,
        rowGroup: false,
        minWidth: 210,
        valueGetter: (params) => {
          const dateValue = params.data ? params.data['lastUpdatedDate'] : null;
          return dateValue
            ? new Date(dateValue + 'Z').toLocaleString('en-US', dateOptions)
            : '';
        },
        filterValueGetter: (params) => {
          return params.data
            ? params.data.lastUpdatedDate
              ? new Date(params.data.lastUpdatedDate + 'Z').toLocaleString(
                  'en-US',
                  dateOptions
                )
              : 'N/A'
            : null;
        },
        comparator: DateComparator,
        headerComponentParams: {
          template: CustomHeaderTemplate('LastUpdatedDate'),
        },
      },
      {
        field: 'editactions',
        headerName: '',
        suppressMenu: true,
        suppressHeaderFilterButton: true,
        suppressSeparator: true,
        sortable: false,
        minWidth: 70,
        maxWidth: 70,
        hide: !currentUserIsSysAdmin && !currentUserIsEngAdmin,
        cellRenderer: MasterDataManagementREITsCellRenderer,
        cellRendererParams: { handleEditButtonClick },
      },
      {
        field: 'deleteactions',
        headerName: '',
        suppressMenu: true,
        suppressHeaderFilterButton: true,
        suppressSeparator: true,
        sortable: false,
        minWidth: 70,
        maxWidth: 70,
        hide: !currentUserIsSysAdmin && !currentUserIsEngAdmin,
        cellRenderer: MasterDataManagementREITsCellRenderer,
        cellRendererParams: { handleDeleteButtonClick },
      },
    ],
    [
      currentUserIsSysAdmin,
      currentUserIsEngAdmin,
      handleEditButtonClick,
      handleDeleteButtonClick,
    ]
  );

  useEffect(() => {
    if (rowData.length > 0) {
      let calcTotalPages =
        gridRef &&
        gridRef.current &&
        gridRef.current.api &&
        gridRef.current.api.paginationGetTotalPages();
      setTotalPages(calcTotalPages);
      //If total page is zero then set the current page as zero
      if (calcTotalPages == 0) {
        setCurrentPage(calcTotalPages);
      }
    }
  }, [rowData]);

  useEffect(() => {
    setRowData(reitsData);
    if (gridRef && gridRef.current && gridRef.current.api) {
      // Get total pages from Grid's default pagination control and apply it to custom pagination control
      let calcTotalPages = gridRef.current.api.paginationGetTotalPages();
      setTotalPages(calcTotalPages);
    }
  }, [reitsData]);

  useEffect(() => {
    //Reset Grid to default column state
    //When Grid Column state changes due to Reset Column State action then Reset Grid to default column state
    let localStorageColumnState = localStorage.getItem(
      'REITSuiteMasterREITDataColumnState'
    );
    if (
      !localStorageColumnState &&
      gridRef &&
      gridRef.current &&
      gridRef.current.api
    ) {
      gridRef.current.api.setColumnDefs([]);
      gridRef.current.api.setColumnDefs(columnDefs);
    }
  }, [gridColumnState]);

  //Handle First Data Renderered event
  const handleFirstDataRendered = useCallback((event) => {
    gridRef.current.columnApi.autoSizeAllColumns();
  }, []);

  //Handle Grid Page change event
  const handlePageChange = useCallback((val) => {
    setCurrentPage(val);
    gridRef.current.api.paginationGoToPage(val - 1);
  }, []);

  const overlayNoRowsTemplate = `<div class="custom-no-rows-template">${instructionText}</div>`;
  const hasData = reitsData && reitsData.length > 0;

  // Determine if the delete button should be disabled
  function isDeleteDisabled(data) {
    const periodsExist =
      Array.isArray(data?.periods) && data.periods.length > 0;
    const dep = data?.reitDependentEntityData || {};

    return (
      periodsExist ||
      dep.checklistDeliveryScheduleExists === true ||
      dep.propertyMasterExists === true ||
      dep.trsMasterExists === true ||
      dep.securitiesMasterExists === true
    );
  }
  const onCellKeyDown = useCallback(
    (event) => {
      if (event.event.key === 'Enter' || event.event.key === ' ') {
        if (event.colDef.field == 'editactions') {
          handleEditButtonClick(event.data);
        }
        if (!isDeleteDisabled(event.data)) {
          if (event.colDef.field == 'deleteactions') {
            handleDeleteButtonClick(event.data);
          }
        }
      }
    },
    [handleEditButtonClick, handleDeleteButtonClick]
  );

  return (
    <React.Fragment>
      <div className="row mb-10">
        <div className="col mt-3">
          <div
            className={`ag-theme-quartz motif-table ${hasData ? 'table-bd-full-height-width' : 'empty-table-container'}`}
            data-testid="divREITsDataGrid"
          >
            <MotifTable
              zebra={true}
              compact={true}
              rowData={rowData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              accentedSort={true}
              rowSelection="multiple"
              suppressRowClickSelection={true}
              onGridReady={handleGridReady}
              onFirstDataRendered={handleFirstDataRendered}
              ref={gridRef}
              pagination={true}
              suppressPaginationPanel={true}
              paginationPageSize={100}
              groupDisplayType={'custom'}
              groupSelectsChildren={true}
              groupDefaultExpanded={0}
              onFilterChanged={handleFilterChanged(
                gridRef,
                currentPage,
                setCurrentPage,
                setTotalPages
              )}
              overlayNoRowsTemplate={overlayNoRowsTemplate}
              onCellKeyDown={onCellKeyDown}
            />

            {rowData.length > 0 && (
              <div className="mt-3">
                <span className="right mb-3">
                  <CustomPagination
                    currentPage={currentPage}
                    handlePageChange={handlePageChange}
                    totalPages={totalPages}
                    handleItemsPerPageChange={handleItemsPerPageChange}
                    gridRef={gridRef}
                    setCurrentPage={setCurrentPage}
                    setItemsPerPage={setItemsPerPage}
                    setTotalPages={setTotalPages}
                  />
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

MasterREITsTable.propTypes = {
  reitsData: PropTypes.array.isRequired,
  gridColumnState: PropTypes.object.isRequired,
  handleEditButtonClick: PropTypes.func.isRequired,
  handleDeleteButtonClick: PropTypes.func.isRequired,
  handleGridChanges: PropTypes.func.isRequired,
  currentUserIsSysAdmin: PropTypes.bool,
  currentUserIsEngAdmin: PropTypes.bool,
};

export default MasterREITsTable;
